import { addYears, differenceInDays, format, isBefore, isMatch, parse, parseISO } from 'date-fns';
import type {
  InlineCarouselAnalyticProps,
  OrganizerItemAnalyticProps,
} from '@/app/interface/analytics.interface';
import { WIDGET_VIEWED } from '@/app/analytics/constants';
import type { OrganizerItemViewComponentMeta } from '@/app/interface/viewmodel.interface';
import { ViewByCategory } from '../interface/viewBy.interface';

export const getInlineCarouselProps = ({
  header,
  category,
  filter,
  vendorCount,
  viewBy = null,
}: InlineCarouselAnalyticProps) => {
  return {
    header,
    vendorCount,
    category,
    filter,
    eventName: WIDGET_VIEWED,
    widgetName: 'organizer recommendations',
    source: 'checklist',
    userDecisionArea: viewBy && viewBy === ViewByCategory ? 'category-view' : 'list view',
  };
};

// We expect the following three formats:
// 1. An empty string indicates "All Months"
// 2. Full month name, e.g. "October"
// 3. Month and year, e.g., "January 2024"
export const formatMonthFilterValue = (date: string) => {
  const monthFormat = 'MMMM';
  const monthAndYearFormat = 'MMMM yyyy';

  let dateObj;
  if (!date || date === '') {
    return 'All Months';
  }

  const isMonthAndYearString = (date: string) => isMatch(date, monthAndYearFormat);
  const isMonthString = (date: string) => isMatch(date, monthFormat);

  if (isMonthAndYearString(date)) {
    dateObj = parse(date, monthAndYearFormat, new Date());
  } else if (isMonthString(date)) {
    dateObj = parse(date, monthFormat, new Date());

    if (isBefore(dateObj, new Date())) {
      dateObj = addYears(dateObj, 1);
    }
  }

  if (!dateObj) {
    return 'invalid date format';
  }

  return format(dateObj, 'yyyy-MM');
};

export const isoDateToMonthYear = (date: string) => {
  if (!date || date === '') return 'invalid date format';
  const dateObj = parseISO(date);
  return format(dateObj, 'MMMM yyyy');
};

export const getOrganizerItemAnalytics = (
  itemFields: OrganizerItemViewComponentMeta,
  section: string,
  showCompleted: boolean,
  vendorCount: number
): OrganizerItemAnalyticProps => {
  const { categoryName, categoryCode, browsingItemShortText, dueBy, isCustomItem, taskItemName } =
    itemFields;

  return {
    category: categoryName || '',
    categoryCode: categoryCode || '',
    ctaText: browsingItemShortText || '',
    decisionArea: 'list view',
    dueBy: dueBy || '',
    dueDateDiff: dueBy ? differenceInDays(new Date(dueBy), new Date()).toString() : '',
    showCompleted: showCompleted ?? false,
    isCustomItem: isCustomItem || false,
    header: taskItemName || '',
    organizerSection: dueBy ? isoDateToMonthYear(dueBy) : '',
    section: section || '',
    filter: 'to-do',
    userDecisionArea: 'list view',
    variationAlgorithm: 'pvrec',
    vendorCount,
  };
};

export const getWeddingCountdown = (dateStr: string) => {
  if (dateStr.length === 0) return '';

  const date = new Date(dateStr);
  if (!date) return '';

  const dateDiff = differenceInDays(date, new Date());
  // invalid dates should return an empty string.
  return dateDiff && !isNaN(dateDiff) ? dateDiff.toString() : '';
};

export const hasOrganizerPrompts = (data: any) => {};

export const getTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
